import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';

// Component to fit map bounds to route
function FitToBounds({ coords }) {
  const map = useMap();
  
  useEffect(() => {
    if (!coords || coords.length === 0) return;
    const bounds = L.latLngBounds(coords);
    map.fitBounds(bounds, { padding: [50, 50] });
  }, [coords, map]);
  
  return null;
}

/**
 * RouteDetailMap - Displays a single route on a map with start/end markers
 * @param {Object} route - Route object with coordinates, startPoint, endPoint
 */
const RouteDetailMap = ({ route }) => {
  // Prepare coordinates for map
  const routeCoordinates = route?.coordinates || [];
  const hasCoordinates = routeCoordinates.length > 0;
  
  // Convert coordinates from [lng, lat] to [lat, lng] for Leaflet
  const leafletCoordinates = hasCoordinates 
    ? routeCoordinates.map(([lng, lat]) => [lat, lng])
    : [];

  // Calculate map center
  const mapCenter = hasCoordinates
    ? leafletCoordinates[Math.floor(leafletCoordinates.length / 2)]
    : [20.5937, 78.9629]; // Default to India center

  if (!hasCoordinates) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800">
        <div className="text-center">
          <svg 
            className="w-16 h-16 text-gray-400 mx-auto mb-3" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" 
            />
          </svg>
          <p className="text-gray-500 dark:text-gray-400">
            No map data available for this route
          </p>
        </div>
      </div>
    );
  }

  return (
    <MapContainer
      center={mapCenter}
      zoom={13}
      className="w-full h-full"
      scrollWheelZoom={true}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <FitToBounds coords={leafletCoordinates} />
      
      {/* Draw the route */}
      <Polyline
        positions={leafletCoordinates}
        pathOptions={{
          color: '#10b981',
          weight: 4,
          opacity: 0.8,
          lineCap: 'round',
          lineJoin: 'round'
        }}
      />
      
      {/* Start marker */}
      <Marker position={leafletCoordinates[0]}>
        <Popup>
          <div className="text-sm">
            <strong>Start:</strong> {route.startPoint || 'Starting Point'}
          </div>
        </Popup>
      </Marker>
      
      {/* End marker */}
      <Marker position={leafletCoordinates[leafletCoordinates.length - 1]}>
        <Popup>
          <div className="text-sm">
            <strong>End:</strong> {route.endPoint || 'Ending Point'}
          </div>
        </Popup>
      </Marker>
    </MapContainer>
  );
};

export default RouteDetailMap;
